import * as React from 'react';
import * as ReactIs from 'react-is';
import {CSSTransition, TransitionGroup} from 'react-transition-group';
import {CSSTransitionClassNames} from 'react-transition-group/CSSTransition';

import {ClassNameBuilder} from '../../utils/ClassNameBuilder';

import * as Styles from './ListGroup.module.scss';
import {ListGroupRequire, ListGroupIgnoreProps} from './ListGroupRequire';
import {ListGroupItemProps, ListGroupItem} from './ListGroupItem';

const defaultAnimationClassNames: CSSTransitionClassNames = {
    enter: Styles['item-enter'],
    enterActive: Styles['item-enter-active'],
    exit: Styles['item-exit'],
    exitActive: Styles['item-exit-active']
};

export interface ListItemProps<T> {
    item: T;
}

interface ListContextValue {
    value: number;
    setValue: (value: number) => void;
}

export const ListContext = React.createContext<ListContextValue>({} as ListContextValue);

export interface ListGroupProps extends React.HTMLAttributes<HTMLUListElement> {
    /**
     * List Item animation classname to override. @see `CSSTransitionClassNames`
     */
    animationClassNames?: CSSTransitionClassNames;
    /**
     * List Item animation time override. @see `CSSTransition`
     *
     * @default 200
     */
    animationTimeout?: number;
    autoScroll?: boolean;
    /**
     * Add a border to the bottom of each list item.
     *
     * @default false
     */
    border?: boolean;
    /**
     * When a list items are changed the first item will be highlighted.
     *
     * @default false
     */
    changeResetsActiveItem?: boolean;
    children?: React.ReactNode;
    className?: string;
    /**
     *  If enabled the first item in the list will be highlighted and active
     *
     * @default false
     */
    firstItemActive?: boolean;
    /**
     * Element to use as a keyboard source
     */
    focusElement?: React.RefObject<HTMLElement>;
    /**
     * Display horizontally.
     *
     * @default false
     */
    horizontal?: boolean;
    itemActiveBackground?: boolean;
    /**
     * If a keyboard source has been set, keycodes that are pressed while this list or `focusElement` is focused
     * will trigger the `onClick` or `onSelect`. An example is pressing the 'Enter' key.
     *
     * @default ['Enter']
     */
    selectKeys?: string[];
}

/* We need to flatten React.Fragment arrays so we can access the elements
 */
export function cleanChildren(children?: React.ReactNode) {
    const childArray = ReactIs.isFragment(children) ? children.props.children.flat() : children;
    return React.Children.toArray(childArray ?? []);
}

export const ListGroup = React.memo(React.forwardRef((props: ListGroupProps, ref: React.Ref<HTMLElement>) => {
    const {
        animationClassNames = defaultAnimationClassNames,
        animationTimeout = 200,
        autoScroll = true,
        border = false,
        changeResetsActiveItem = false,
        children = [],
        className,
        firstItemActive = false,
        focusElement,
        horizontal = false,
        itemActiveBackground = true,
        selectKeys = ['Enter'],
        ...rest
    } = props;
    const classNames = new ClassNameBuilder(Styles, 'list-group')
        .add(['horizontal', horizontal])
        .add(className)
        .toString();
    const listElements = cleanChildren(children);
    let count = 0;
    listElements.forEach((x) => {
        if (x.type === ListGroupItem) {
            count++;
        }
    });

    const activeListElementRef = React.useRef<HTMLElement>(null);
    const [highlightedIndex, setHighlightedIndex, listGroupKeyboardRef] = useKeyboardNavigate(
        activeListElementRef,
        count,
        focusElement,
        selectKeys,
        horizontal,
        firstItemActive,
        changeResetsActiveItem
    );
    const setupRefs = React.useCallback((node: HTMLElement | null) => {
        if (node && ref) {
            if (ref.hasOwnProperty('current')) {
                (ref as React.MutableRefObject<HTMLElement>).current = node;
            } else {
                (ref as Function)(node);
            }
        }

        listGroupKeyboardRef(node);
    }, [ref]);

    const contextValue: ListContextValue = {
        value: highlightedIndex,
        setValue: setHighlightedIndex
    };

    return (
        <ListContext.Provider value={contextValue}>
            <ul className={classNames} ref={setupRefs} tabIndex={0} {...rest}>
                <TransitionGroup component={null}>
                    {
                        React.useMemo(() => {
                            // Shift the indexes by amount of ignore elements that have previously been rendered
                            let shiftedLeftAmount = 0;
                            return listElements.map((child: React.ReactElement<ListItemProps<unknown>>, keyIndex: number) => {
                                if (child.type === ListGroupRequire) {
                                    shiftedLeftAmount -= 1;
                                    // Remove ignore components like seperators if they require items before or after
                                    const {requireAfter = false, requireBefore = false} = child.props as Omit<ListGroupIgnoreProps, 'children'>;
                                    const previous = listElements[keyIndex - 1];
                                    const next = listElements[keyIndex + 1];
                                    // A key has to be given to the Fragment so it can remove the old node
                                    if (requireAfter) {
                                        if (!next || next.type === ListGroupRequire) {
                                            return <React.Fragment key={child.key || keyIndex}></React.Fragment>;
                                        }
                                    }
                                    if (requireBefore) {
                                        if (!previous || previous.type === ListGroupRequire) {
                                            return <React.Fragment key={child.key || keyIndex}></React.Fragment>;
                                        }
                                    }
                                    return child;
                                }
                                if (child.type !== ListGroupItem) {
                                    shiftedLeftAmount -= 1;
                                    return child;
                                }
                                const index = keyIndex + shiftedLeftAmount;
                                const isActive = highlightedIndex === index;
                                const childProps: Omit<ListGroupItemProps<unknown>, 'item' | 'children'> = {
                                    activeRef: isActive ? activeListElementRef : undefined,
                                    autoScroll,
                                    border,
                                    itemActiveBackground,
                                    horizontal,
                                    index
                                };
                                const key = child?.key ?? keyIndex;
                                return (
                                    <CSSTransition key={`${key}transition`} timeout={animationTimeout} classNames={animationClassNames}>
                                        {React.cloneElement(child, {...child.props, ...childProps})}
                                    </CSSTransition>
                                );
                            });
                        }, [listElements, highlightedIndex])
                    }
                </TransitionGroup>
            </ul>
        </ListContext.Provider>
    );
}));

// index, setIndex, setRef
type UseKeyboardNavigateReturn = [number, (index: number) => void, (node: HTMLElement | null) => void];

function useKeyboardNavigate(
    activeItem: React.RefObject<HTMLElement | null>,
    length: number,
    keyboardSourceRef: React.RefObject<HTMLElement> | undefined,
    selectKeys: string[],
    horizontal: boolean,
    firstItemActive: boolean,
    resetActiveItemOnChange: boolean
): UseKeyboardNavigateReturn {
    const startingIndex = firstItemActive ? 0 : -1;
    const countRef = React.useRef(length);
    countRef.current = length;
    // Use a reducer to simplify state management and reduce re-renders
    const [[index], dispatchIndex] = React.useReducer(indexReducer, [startingIndex, -2, length - 1]);
    const indexRef = React.useRef(index);
    indexRef.current = index;
    React.useEffect(() => {
        dispatchIndex({type: 'set-max', value: length - 1, first: resetActiveItemOnChange ? startingIndex : undefined});
        if (resetActiveItemOnChange) {
            dispatchIndex({type: 'set-index', value: startingIndex});
        }
    }, [length]);

    const handleKeydown = React.useCallback((event: KeyboardEvent) => {
        if (horizontal) {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                dispatchIndex({type: 'decrement'});
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                dispatchIndex({type: 'increment'});
            }
        } else {
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                dispatchIndex({type: 'decrement'});
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                dispatchIndex({type: 'increment'});
            }
        }

        if (selectKeys.some((key) => key === event.key)) {
            if (!event.repeat) {
                activeItem.current?.click?.();
            }
        }
    }, []);

    // Add keyboard events to an extra source that has focus. E.g. a input field
    useAddEventListener('keydown', handleKeydown, keyboardSourceRef);
    // Create a callback that will be passed to an element as a ref so we can listen to events.
    const ref: React.MutableRefObject<HTMLElement | null> = React.useRef<HTMLElement>(null);
    const setRef = React.useCallback((node) => {
        if (ref?.current) {
            ref.current.removeEventListener('keydown', handleKeydown);
        }
        if (node) {
            node.addEventListener('keydown', handleKeydown);
        }
        ref.current = node;
    }, []);
    const setIndex = (value: number) => {
        if (value !== index) {
            dispatchIndex({type: 'set-index', value});
        }
    };

    return [index, setIndex, setRef];
}

type Action<T extends string, P = {}> = {type: T} & P;

type IndexAction =
    | Action<'increment'>
    | Action<'decrement'>
    | Action<'set-index', {value: number}>
    | Action<'contain'>
    | Action<'set-max', {value: number; first?: number}>;

const indexReducer: React.Reducer<[number, number, number], IndexAction> = (state, action) => {
    const [current, previous, max] = state;
    switch (action.type) {
        case 'decrement':
            return [cyclicClamp(0, max, current - 1), current, max];
        case 'increment':
            return [cyclicClamp(0, max, current + 1), current, max];
        case 'set-index':
            return [action.value, current, max];
        case 'set-max':
            // eslint-disable-next-line no-case-declarations
            let newIndex: [number, number] = [current, previous];
            if (current > action.value) {
                newIndex = [action.first ?? action.value, current];
            }
            return [newIndex[0], newIndex[1], action.value];
    }
    return state;
};

const useAddEventListener = (event: string, handler: (e: any) => void, ref?: React.RefObject<HTMLElement | null>, ...dependencies: any[]) => {
    React.useEffect(() => {
        const a = handler;
        ref?.current?.addEventListener(event, a);
        return () => {
            ref?.current?.removeEventListener(event, a);
        };
    }, [...dependencies]);
};

const cyclicClamp = (min: number, max: number, value: number): number => {
    if (value < min) {
        return max;
    }
    if (value > max) {
        return min;
    }
    return value;
};
